import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import {
  Routes,
  makeSimpleRoutePath,
  makeSendMessageDetailPath
} from '../../utils/routerHelper';
import { formatAdminDateTime, competitionTimeZone } from '../../utils/adminDateTime';
import ScheduledStatusPanel from './ScheduledStatusPanel';
import { BroadcastRow, ScheduledCommunicationsStatus } from './scheduledTypes';

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  canViewAdminTools: boolean;
  canViewCommsTools: boolean;
  competitionTimezone?: string;
}

interface LocalState {
  status: ScheduledCommunicationsStatus | null;
  statusLoading: boolean;
  statusError: string;
  broadcasts: BroadcastRow[];
  broadcastsLoading: boolean;
  broadcastsError: string;
}

const cardStyle: React.CSSProperties = {
  padding: 20,
  marginBottom: 16
};

/**
 * Communications Center
 *
 * Access model (do not auto-broaden):
 * - canViewAdminTools: full hub (announcements, scheduled status, templates, notifications, help, recent, heartbeat).
 * - canViewCommsTools only (division captains): slim "Division announcements" card → Send Message.
 *   Captains keep Send Message in the profile menu; they are not given the full hub nav entry.
 *   Admin Guide / Email Templates / scheduled-status remain admin-only (not auto-broadened).
 */
class CommunicationsPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    status: null,
    statusLoading: false,
    statusError: '',
    broadcasts: [],
    broadcastsLoading: false,
    broadcastsError: ''
  };

  public componentDidMount() {
    const { canViewAdminTools, canViewCommsTools } = this.props;
    if (canViewAdminTools) {
      this.loadScheduledStatus();
      this.loadRecentBroadcasts();
    } else if (!canViewCommsTools) {
      // gate below in render
    }
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private formatWhen = (iso: string) =>
    formatAdminDateTime(iso, this.props.competitionTimezone)

  private loadScheduledStatus = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ statusLoading: true, statusError: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/communications/scheduled-status`,
        { headers: this.authHeaders() }
      );
      if (res.status === 404) {
        this.setState({
          statusLoading: false,
          statusError: 'Scheduled status is not available on this API yet.'
        });
        return;
      }
      if (res.status === 403) {
        this.setState({ statusLoading: false, statusError: 'You do not have permission to view scheduled status.' });
        return;
      }
      if (!res.ok) {
        this.setState({ statusLoading: false, statusError: `Failed to load scheduled status (${res.status}).` });
        return;
      }
      const status = await res.json();
      this.setState({ status, statusLoading: false });
    } catch (_err) {
      this.setState({ statusLoading: false, statusError: 'Network error loading scheduled status.' });
    }
  }

  private loadRecentBroadcasts = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ broadcastsLoading: true, broadcastsError: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/broadcasts`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) {
        this.setState({ broadcastsLoading: false, broadcastsError: 'Failed to load recent communications.' });
        return;
      }
      const data = await res.json();
      const broadcasts: BroadcastRow[] = (data.broadcasts || []).slice(0, 8);
      this.setState({ broadcasts, broadcastsLoading: false });
    } catch (_err) {
      this.setState({ broadcastsLoading: false, broadcastsError: 'Network error loading recent communications.' });
    }
  }

  private renderCaptainSlim = () => {
    const { t } = this.props;
    return (
      <div className="cc__white_box" style={cardStyle}>
        <h3>{t('Division announcements')}</h3>
        <p className="cc__lighttext">
          {t('As a division captain you can send announcements to your division and review message history. Competition-wide templates and scheduled lifecycle email are managed by competition administrators.')}
        </p>
        <Link to={makeSimpleRoutePath(Routes.SendMessage, t)}>
          <Button color="primary">{t('Send Message')}</Button>
        </Link>
      </div>
    );
  }

  private renderAdminHub = () => {
    const { t, competitionTimezone } = this.props;
    const {
      status, statusLoading, statusError,
      broadcasts, broadcastsLoading, broadcastsError
    } = this.state;
    const check = status && status.lastCommsCheck;

    return (
      <div>
        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Send an announcement')}</h3>
          <p className="cc__lighttext">
            {t('Compose a competition, department, or division announcement. Recipients receive in-app and/or email based on your channel choice and their preferences.')}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to={makeSimpleRoutePath(Routes.SendMessage, t)}>
              <Button color="primary">{t('Compose')}</Button>
            </Link>
            <Link to={makeSimpleRoutePath(Routes.SendMessage, t)}>
              <Button color="secondary">{t('View sent messages')}</Button>
            </Link>
          </div>
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Scheduled communications')}</h3>
          <p className="cc__lighttext">
            {t('Signup confirmation and launch welcome timing. Challenge start is when the competition opens; welcome email may send at a configured later time.')}
          </p>
          <ScheduledStatusPanel
            status={status}
            loading={statusLoading}
            error={statusError}
            t={t}
            timeZone={competitionTimezone}
            detailed={false}
          />
          <div style={{ marginTop: 12 }}>
            <Link to={makeSimpleRoutePath(Routes.CommunicationsScheduled, t)}>
              <Button color="secondary" size="sm">{t('View schedule details')}</Button>
            </Link>
            {' '}
            <Button color="link" size="sm" onClick={this.loadScheduledStatus} disabled={statusLoading}>
              {t('Refresh')}
            </Button>
          </div>
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Email templates')}</h3>
          <p className="cc__lighttext">
            {t('Edit preregistration and notification email templates, send tests, and optionally trigger lifecycle batches.')}
          </p>
          <Link to={makeSimpleRoutePath(Routes.EmailTemplates, t)}>
            <Button color="secondary">{t('Manage templates')}</Button>
          </Link>
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Notifications')}</h3>
          <p className="cc__lighttext">
            {t('View your in-app notification feed (likes, comments, flags, announcements, and more).')}
          </p>
          <Link to={makeSimpleRoutePath(Routes.Notifications, t)}>
            <Button color="secondary">{t('View notifications')}</Button>
          </Link>
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Help')}</h3>
          <p className="cc__lighttext">
            {t('Open the Admin Guide communications section for timing, preferences, and troubleshooting.')}
          </p>
          <AdminGuideLink sectionId="communications" label={t('Admin Guide — Communications')} t={t} />
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Recent communications')}</h3>
          {broadcastsError && <Alert color="warning">{broadcastsError}</Alert>}
          {broadcastsLoading && <p className="cc__lighttext">{t('Loading…')}</p>}
          {!broadcastsLoading &&
            <Table responsive={true} size="sm">
              <thead>
                <tr>
                  <th>{t('When')}</th>
                  <th>{t('Subject')}</th>
                  <th>{t('Audience')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map((row) =>
                  <tr key={row.id}>
                    <td>{this.formatWhen(row.sentAt || row.createdAt)}</td>
                    <td>
                      <Link to={makeSendMessageDetailPath(row.id, t)}>{row.subject}</Link>
                    </td>
                    <td>{row.audienceType}</td>
                    <td>{row.status}</td>
                  </tr>
                )}
                {broadcasts.length === 0 &&
                  <tr>
                    <td colSpan={4} className="cc__lighttext">{t('No messages sent yet.')}</td>
                  </tr>
                }
              </tbody>
            </Table>
          }
        </div>

        <div className="cc__white_box" style={cardStyle}>
          <h3>{t('Automation heartbeat')}</h3>
          {!check && !statusLoading &&
            <p className="cc__lighttext">{t('No automated communications check recorded yet.')}</p>
          }
          {check &&
            <div>
              <p>
                {t('Last automated communications check')}:{' '}
                <strong>{this.formatWhen(check.completedAt || check.startedAt || '')}</strong>
                {check.result ? ` (${check.result})` : ''}
              </p>
              {check.welcomeSent != null &&
                <p className="cc__lighttext">
                  {t('Last run sent')}: {check.welcomeSent || 0} {t('welcome')}
                </p>
              }
              {check.stale &&
                <Alert color="warning">
                  {t('This check looks stale (more than about 90 minutes since the last successful run). The hourly scheduler may not be running.')}
                </Alert>
              }
            </div>
          }
        </div>
      </div>
    );
  }

  public render() {
    const { t, canViewAdminTools, canViewCommsTools } = this.props;

    if (!canViewAdminTools && !canViewCommsTools) {
      return (
        <div>
          <div className="container" style={{ maxWidth: 960, paddingTop: 24, paddingBottom: 40 }}>
            <Alert color="danger">{t('You do not have permission to view Communications.')}</Alert>
          </div>
          <Footer />
        </div>
      );
    }

    return (
      <div>
        <div className="container" style={{ maxWidth: 960, paddingTop: 24, paddingBottom: 40 }}>
          <h2>{t('Communications')}</h2>
          <p className="cc__lighttext">
            {canViewAdminTools
              ? t('Announcements, scheduled lifecycle email, templates, and notification tools for this Challenge.')
              : t('Division announcement tools for captains.')}
          </p>
          {canViewAdminTools ? this.renderAdminHub() : this.renderCaptainSlim()}
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  canViewAdminTools: selectors.canViewAdminTools(state),
  canViewCommsTools: selectors.canViewCommsTools(state),
  competitionTimezone: competitionTimeZone(state.competition as { timezone?: string } | null)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(CommunicationsPage));
